import { z } from 'zod';
import { FileInfo } from './files.js';
import { PersonRef } from './people.js';
import { Priority, StatusCategory, WorkItemType } from './work-items.js';
import { FieldValues } from './work-layout.js';
import { ActivityRuleRef } from './rules.js';
export const StatusColor = z.enum(['slate', 'blue', 'violet', 'red', 'green', 'orange']);
export const WorkStatus = z.object({
    id: z.uuid(),
    key: z.string(),
    label: z.string(),
    category: StatusCategory,
    color: StatusColor,
    position: z.number().int(),
    types: z.array(WorkItemType).nullable(),
    /** A stage one project added (docs/adr/0093); null or absent for the workspace's statuses. */
    projectId: z.uuid().nullable().optional(),
});
export const StatusList = z.object({ items: z.array(WorkStatus) });
export const ReplaceStatusesInput = z.object({
    items: z
        .array(z.object({
        id: z.uuid().optional(),
        key: z.string().regex(/^[a-z][a-z0-9_]{0,31}$/),
        label: z.string().trim().min(1).max(32),
        category: StatusCategory,
        color: StatusColor,
        types: z.array(WorkItemType).nullable(),
    }))
        .min(2)
        .max(20),
});
export const Visibility = z.enum(['internal', 'client']);
export const WorkItemSummary = z.object({
    id: z.uuid(),
    key: z.string(),
    type: WorkItemType,
    title: z.string(),
    statusId: z.uuid(),
    priority: Priority,
    assignee: PersonRef.nullable(),
    dueDate: z.string().nullable(),
    slaDueAt: z.string().nullable(),
    /** Support tickets: first-response target, when it was met, and whether the SLA is paused (waiting on client). */
    firstResponseDueAt: z.string().nullable(),
    firstRespondedAt: z.string().nullable(),
    slaPaused: z.boolean(),
    estimateMinutes: z.number().int().nullable(),
    loggedMinutes: z.number().int(),
    commentCount: z.number().int(),
    /** Null for a task without a project (it shows under its client, or "No project"). */
    projectId: z.uuid().nullable(),
    /** Empty when there is no project. */
    projectName: z.string(),
    /** Null for a task in a team channel (such as #general) with no client. */
    clientId: z.uuid().nullable(),
    /** Empty when there is no client. */
    clientName: z.string(),
    requesterEmail: z.string().nullable(),
    labels: z.array(z.string()),
    position: z.number(),
    completedAt: z.string().nullable(),
    updatedAt: z.string(),
    /** The task's section in its project, null for "No section" (docs/adr/0093). */
    sectionId: z.uuid().nullable().optional(),
    /** Custom column values by field id: in a project's list and a task's detail, for the team only (docs/adr/0093). */
    fields: FieldValues.optional(),
});
export const Subtask = z.object({ id: z.uuid(), title: z.string(), done: z.boolean(), position: z.number().int() });
export const ActivityEntry = z.object({
    id: z.uuid(),
    /** `repo`: a linked commit or pull request (docs/adr/0052); `actor` is null and `text` names the author. */
    /** `rule`: a project rule ran (docs/adr/0109); `actor` is null, `rule` names it and `text` says what it did and why. */
    kind: z.enum(['created', 'status', 'assigned', 'time', 'comment', 'updated', 'repo', 'rule']),
    actor: PersonRef.nullable(),
    text: z.string(),
    rule: ActivityRuleRef.nullable().optional(),
    createdAt: z.string(),
});
export const SourceMessage = z.object({
    messageId: z.uuid(),
    channelId: z.uuid(),
    channelName: z.string(),
    author: PersonRef.nullable(),
    body: z.string(),
    createdAt: z.string(),
});
/** Where a task is live (docs/adr/0089). Its commits are the task's linked commits that arrived after it was marked. */
export const ReleaseEnvironment = z.enum(['staging', 'uat', 'production']);
export const ReleaseCommit = z.object({
    sha: z.string(),
    /** The first line of the commit message only. */
    title: z.string(),
    url: z.string().nullable(),
    authorName: z.string(),
    at: z.string(),
});
export const Release = z.object({
    id: z.uuid(),
    environment: ReleaseEnvironment,
    deployedAt: z.string(),
    deployedBy: PersonRef.nullable(),
    commits: z.array(ReleaseCommit),
});
export const ReleaseList = z.object({ items: z.array(Release) });
export const MarkDeployedInput = z.object({ environment: ReleaseEnvironment });
/** Where a comment or time note goes: the task, the task's channel, or both (docs/adr/0089). */
export const CommentDestination = z.enum(['task', 'both', 'chat']);
/** A comment on a task. `via` says where it went: 'chat' came from the channel, 'task' stayed here, 'both' went to both. */
export const TaskComment = z.object({
    id: z.uuid(),
    author: PersonRef.nullable(),
    /** A small, safe subset of HTML: bold, italic, lists, code, links and line breaks. */
    body: z.string(),
    via: z.enum(['chat', 'task', 'both']),
    createdAt: z.string(),
    /** The comment this one answers, shown as a quote (replies are not nested). */
    quote: z.object({ id: z.uuid(), author: PersonRef.nullable(), excerpt: z.string() }).nullable(),
    files: z.array(FileInfo),
});
export const CreateTaskCommentInput = z.object({
    body: z.string().trim().min(1).max(20000),
    destination: CommentDestination.default('both'),
    quoteId: z.uuid().optional(),
    /** Finished uploads of the caller; they are attached to the task. */
    fileIds: z.array(z.uuid()).max(10).optional(),
    /** People mentioned as `@Name` in the body (docs/adr/0092): they are told, and the chat copy carries `<@id>` tokens. */
    mentionUserIds: z.array(z.uuid()).max(20).optional(),
});
export const WorkItemDetail = WorkItemSummary.extend({
    /**
     * The description as stored. Written in the drawer it is the small sanitised HTML subset of docs/adr/0089 (bold,
     * italic, underline, lists, code, quotes, safe links); older descriptions are plain text. `descriptionIsHtml` tells
     * them apart and plain text is rendered as paragraphs (docs/adr/0092).
     */
    description: z.string(),
    visibility: Visibility,
    reporter: PersonRef.nullable(),
    subtasks: z.array(Subtask),
    activity: z.array(ActivityEntry),
    source: SourceMessage.nullable(),
    /** The message whose replies are this item's comments. */
    conversationMessageId: z.uuid().nullable(),
    channelId: z.uuid().nullable(),
    /** Time on this task counts against the client's support plan. */
    countsToPlan: z.boolean(),
    /** The client's rating of a resolved ticket. */
    satisfaction: z.enum(['great', 'ok', 'bad']).nullable(),
    /** Where it went live (for example "UAT" or "Production"), when, and a note (docs/adr/0088). */
    deployedTo: z.string().nullable(),
    deployedAt: z.string().nullable(),
    deployNotes: z.string(),
    /** The estimate (and line) the task was quoted under (docs/adr/0088). Null for people without the Invoicing status level. */
    estimateId: z.uuid().nullable(),
    estimateLineId: z.uuid().nullable(),
    /** Where the task came from: web, mobile, email, chat, import or mcp. */
    createdVia: z.enum(['web', 'mobile', 'email', 'chat', 'import', 'mcp']),
    attachments: z.array(FileInfo),
    /** Who looks the work over when it is ready for review (docs/adr/0089). */
    reviewer: PersonRef.nullable(),
    /** One entry per environment the task has been marked deployed to, oldest first. */
    releases: z.array(Release),
    /** Comments written in the drawer plus replies in the task's channel, oldest first (chat-only comments are left out). */
    comments: z.array(TaskComment),
    createdAt: z.string(),
    /** The caller follows this item (docs/adr/0074): they keep seeing it whatever their project membership. */
    following: z.boolean().optional(),
    /** Who follows the item (docs/adr/0092). Staff only; a guest gets none. */
    followers: z.array(PersonRef).optional(),
    /** Where a new comment goes unless the person picks otherwise: the project's `tasks.commentDestination` (docs/adr/0092). */
    defaultCommentDestination: z.enum(['task', 'both']).optional(),
    /** When this support ticket was merged into another (docs/adr/0102); null when it wasn't. Staff only. */
    mergedAt: z.string().nullable().optional(),
    /** The ticket it was merged into, when the caller may see it. Staff only. */
    mergedInto: z.object({ id: z.uuid(), key: z.string(), title: z.string() }).nullable().optional(),
    /**
     * The merge that can be undone from this (merged) ticket (docs/adr/0103): set when its merge was recorded and the
     * caller may undo it. Null for merges from before undo existed, so the action is hidden. Staff only.
     */
    mergeUndo: z.object({ mergeId: z.uuid() }).nullable().optional(),
    /** Tickets merged into this one, oldest merge first; `mergeId` when that merge can be undone (docs/adr/0103). Staff only. */
    mergedFrom: z.array(z.object({ id: z.uuid(), key: z.string(), title: z.string(), mergeId: z.uuid().nullable().optional() })).optional(),
});
/** Add a staff member as a follower of a work item (docs/adr/0092). */
export const AddFollowerInput = z.object({ userId: z.uuid() });
export const FollowerList = z.object({ items: z.array(PersonRef) });
export const WorkItemList = z.object({ items: z.array(WorkItemSummary) });
/** Follow or stop following a work item you can see (docs/adr/0074). */
export const FollowWorkItemInput = z.object({ following: z.boolean() });
export const FollowWorkItemResponse = z.object({ following: z.boolean() });
export const WorkItemQuery = z.object({
    projectId: z.uuid().optional(),
    clientId: z.uuid().optional(),
    /** Items made in this channel (a team channel such as #general has no project or client). */
    channelId: z.uuid().optional(),
    types: z.string().optional(), // comma list
    assignee: z.union([z.literal('me'), z.uuid()]).optional(),
    open: z.enum(['true', 'false']).optional(),
    q: z.string().max(100).optional(),
});
/** Deploy details and the estimate link (docs/adr/0088). `deployedAt` is an ISO date-time. */
const DeployFields = {
    deployedTo: z.string().trim().min(1).max(40).nullable().optional(),
    deployedAt: z.iso.datetime().nullable().optional(),
    deployNotes: z.string().max(2000).optional(),
    estimateId: z.uuid().nullable().optional(),
    estimateLineId: z.uuid().nullable().optional(),
};
export const CreateWorkItemInput = z.object({
    /** Leave out for a task without a project. Tickets and items in a project channel still name one. */
    projectId: z.uuid().nullable().optional(),
    /** A task without a project: its client (a client channel's). Ignored when a project is given. */
    clientId: z.uuid().nullable().optional(),
    /** The channel it was made in: status changes post back there. */
    channelId: z.uuid().optional(),
    type: WorkItemType.default('task'),
    title: z.string().trim().min(1).max(200),
    description: z.string().max(20000).optional(),
    statusId: z.uuid().optional(),
    priority: Priority.default('p3'),
    assigneeUserId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
    estimateMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    labels: z.array(z.string().trim().min(1).max(32)).max(10).optional(),
    visibility: Visibility.default('internal'),
    requesterEmail: z.email().nullable().optional(),
    /** Turn an existing chat message into this work item. */
    sourceMessageId: z.uuid().optional(),
    /** A section of the item's project (docs/adr/0093). */
    sectionId: z.uuid().nullable().optional(),
    ...DeployFields,
});
export const UpdateWorkItemInput = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(20000).optional(),
    statusId: z.uuid().optional(),
    priority: Priority.optional(),
    assigneeUserId: z.uuid().nullable().optional(),
    dueDate: z.iso.date().nullable().optional(),
    estimateMinutes: z.number().int().min(0).max(100000).nullable().optional(),
    labels: z.array(z.string().trim().min(1).max(32)).max(10).optional(),
    visibility: Visibility.optional(),
    /** Time on this task counts against the client's support plan (docs/adr/0027). */
    countsToPlan: z.boolean().optional(),
    /** Board ordering within a status column. */
    position: z.number().optional(),
    /** Who reviews the work; null clears it (docs/adr/0089). */
    reviewerUserId: z.uuid().nullable().optional(),
    /** Move a task or bug to another project, or to none. It gets that project's next number, so its key changes (docs/adr/0089). */
    projectId: z.uuid().nullable().optional(),
    /** A section of the item's project, or null for No section (docs/adr/0093). Moving to another project clears it. */
    sectionId: z.uuid().nullable().optional(),
    ...DeployFields,
});
export const CreateSubtaskInput = z.object({ title: z.string().trim().min(1).max(200) });
/** `position` moves the subtask to that index among its siblings (docs/adr/0092). */
export const UpdateSubtaskInput = z.object({ title: z.string().trim().min(1).max(200).optional(), done: z.boolean().optional(), position: z.number().int().min(0).max(1000).optional() });
export const CommentInput = z.object({ body: z.string().trim().min(1).max(10000) });
export const TimeEntry = z.object({
    id: z.uuid(),
    workItemId: z.uuid().nullable(),
    /** Null for time on a task without a project. */
    projectId: z.uuid().nullable(),
    user: PersonRef,
    minutes: z.number().int(),
    date: z.string(),
    note: z.string(),
    createdAt: z.string(),
    /** Billable unless marked otherwise (docs/adr/0054; only shown while the workspace tracks it). */
    billable: z.boolean().optional(),
    /**
     * On an approved invoice: locked (edits and deletes answer 409) until it is voided or credited. The invoice's
     * number when the viewer may see invoice status, else "an invoice". Null when not billed.
     */
    billedOn: z.string().nullable().optional(),
});
export const LogTimeInput = z.object({
    workItemId: z.uuid().optional(),
    projectId: z.uuid().optional(),
    minutes: z.number().int().min(1).max(24 * 60),
    date: z.iso.date().optional(),
    note: z.string().max(500).optional(),
    /** Only asked while the workspace tracks billable time; otherwise every entry is billable. */
    billable: z.boolean().optional(),
    /** Where the note goes: the task's activity, the task's channel, or both (default: the task only, as before). */
    destination: CommentDestination.optional(),
}).refine((v) => v.workItemId || v.projectId, { message: 'workItemId or projectId is required' });
export const TimeEntryList = z.object({ items: z.array(TimeEntry), totalMinutes: z.number().int() });
/** The caller's one running timer (docs/adr/0092). */
export const RunningTimer = z.object({
    workItemId: z.uuid(),
    workItemKey: z.string(),
    workItemTitle: z.string(),
    projectId: z.uuid().nullable(),
    startedAt: z.string(),
    note: z.string(),
});
export const RunningTimerResponse = z.object({ timer: RunningTimer.nullable() });
export const StartTimerInput = z.object({ workItemId: z.uuid(), note: z.string().max(500).optional() });
export const StopTimerInput = z.object({
    note: z.string().max(500).optional(),
    /** Where the note goes (docs/adr/0089); the task's activity by default. */
    destination: CommentDestination.optional(),
    billable: z.boolean().optional(),
    /** Throw the time away instead of logging it. */
    discard: z.boolean().optional(),
});
/** `entry` is null when the time was discarded (asked for, or under a minute). */
export const StopTimerResponse = z.object({ entry: TimeEntry.nullable() });
/** Prefix of the keys of tasks without a project ("T-12"). Project tasks use the project key, tickets "#1001". */
export const NO_PROJECT_KEY = 'T';
/** A work item's key: "#1001" for a ticket, "WEB-12" in a project, "T-12" for a task without a project. */
export function workItemKey(type, number, projectKey, hasProject) {
    if (type === 'support')
        return `#${number}`;
    return hasProject ? `${projectKey ?? '?'}-${number}` : `${NO_PROJECT_KEY}-${number}`;
}
/** Filter and group value for a task's project: the project id, or "none:<client id>" (or "none:") without one. */
export function projectGroupKey(item) {
    return item.projectId ?? `none:${item.clientId ?? ''}`;
}
/** What people read for a task's project: its name, or "Client · No project", or "No project". */
export function projectGroupLabel(item, withClient = true) {
    if (item.projectId)
        return item.projectName;
    return withClient && item.clientName ? `${item.clientName} · No project` : 'No project';
}
/** Parses chat-style durations: "1.5h", "90m", "1h 30m", "2". */
const HOURS = '(?:h|hr|hrs|hour|hours)';
const MINUTES = '(?:m|min|mins|minute|minutes)';
const DURATION = new RegExp(`^(?:(\\d+(?:\\.\\d+)?)\\s*${HOURS})?\\s*(?:(\\d+)\\s*${MINUTES})?$`);
/** "1h30": minutes after hours need no unit. */
const HOURS_THEN_NUMBER = new RegExp(`^(\\d+)\\s*${HOURS}\\s*(\\d+)$`);
/**
 * Minutes from what people type for a duration: "1.5" or "1.5h", "90m", "1h 30m", "1h30", "1:30",
 * "2 hours", "45 mins". Null when it is not a duration.
 */
export function parseDuration(input) {
    const s = input.trim().toLowerCase();
    if (/^\d+(\.\d+)?$/.test(s))
        return Math.round(parseFloat(s) * 60);
    const clock = s.match(/^(\d+):([0-5]\d)$/);
    if (clock)
        return parseInt(clock[1], 10) * 60 + parseInt(clock[2], 10);
    const hm = s.match(HOURS_THEN_NUMBER);
    if (hm)
        return parseInt(hm[1], 10) * 60 + parseInt(hm[2], 10);
    const m = s.match(DURATION);
    if (!m || (!m[1] && !m[2]))
        return null;
    return Math.round((m[1] ? parseFloat(m[1]) * 60 : 0) + (m[2] ? parseInt(m[2], 10) : 0));
}
/** Groups for the "group by due date" view (Muhammad's request, behind tasks.groupByDueDate). */
export const DueBucket = z.enum(['overdue', 'today', 'this_week', 'next_week', 'later', 'no_date', 'done']);
export function dueBucket(dueDate, today, weekStartsOn = 1, done = false) {
    if (done)
        return 'done';
    if (!dueDate)
        return 'no_date';
    if (dueDate < today)
        return 'overdue';
    if (dueDate === today)
        return 'today';
    const t = new Date(`${today}T00:00:00Z`);
    const dow = (t.getUTCDay() - weekStartsOn + 7) % 7;
    const endOfWeek = new Date(t);
    endOfWeek.setUTCDate(t.getUTCDate() + (6 - dow));
    const endOfNext = new Date(endOfWeek);
    endOfNext.setUTCDate(endOfWeek.getUTCDate() + 7);
    const iso = (d) => d.toISOString().slice(0, 10);
    if (dueDate <= iso(endOfWeek))
        return 'this_week';
    if (dueDate <= iso(endOfNext))
        return 'next_week';
    return 'later';
}
/** A task description as plain text: descriptions written in the drawer's editor are small, sanitised HTML; older ones are plain text. */
export const descriptionIsHtml = (d) => /^\s*<(p|div|b|strong|i|em|u|ul|ol|li|code|br|a|blockquote)\b/i.test(d);
export function descriptionToText(d) {
    if (!descriptionIsHtml(d))
        return d;
    return d
        .replace(/<li>/gi, '- ')
        .replace(/<\/(p|div|li|blockquote)>|<br\s*\/?>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&(?:amp|lt|gt|quot|nbsp|#39);/g, (e) => ({ '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ' }[e] ?? e))
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
//# sourceMappingURL=work.js.map